import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  ViewEncapsulation,
  booleanAttribute,
} from '@angular/core';

export const DS_SCROLL_CLASS = 'ds-scroll';
export type DsScrollMode = 'normal' | 'inverse';

// Fallbacks explicitos: si el host no carga variables.css (--ds-scroll-*), el thumb debe verse igual.
export const DS_SCROLL_STYLES = `
  .ds-scroll {
    --ds-scroll-resolved-thumb-color:
      var(--ds-scroll-thumb-color, rgba(32, 63, 128, 0.2));
    --ds-scroll-resolved-thumb-hover-color:
      var(--ds-scroll-thumb-hover-color, rgba(32, 63, 128, 0.28));
    --ds-scroll-resolved-track-color:
      var(--ds-scroll-track-color, rgba(32, 63, 128, 0.06));
    scrollbar-color:
      var(--ds-scroll-resolved-thumb-color)
      var(--ds-scroll-resolved-track-color);
    scrollbar-width: thin;
  }

  .ds-scroll.ds-scroll--inverse {
    --ds-scroll-resolved-thumb-color:
      var(--ds-scroll-inverse-thumb-color, rgba(202, 215, 242, 0.2));
    --ds-scroll-resolved-thumb-hover-color:
      var(--ds-scroll-inverse-thumb-hover-color, rgba(202, 215, 242, 0.28));
    --ds-scroll-resolved-track-color:
      var(--ds-scroll-inverse-track-color, rgba(202, 215, 242, 0.06));
  }

  .ds-scroll--draggable {
    cursor: grab;
    touch-action: auto;
    user-select: none;
    -webkit-user-select: none;
  }

  /* Touch always uses native scrolling (see class doc comment below), so this only
     ever affects mouse/pen dragging. overscroll-behavior-x keeps an edge overscroll
     from chaining into a browser back/forward navigation. */
  .ds-scroll.ds-scroll--elastic {
    overscroll-behavior-x: none;
  }

  .ds-scroll.ds-scroll--snap-enabled.ds-scroll--dragging,
  .ds-scroll.ds-scroll--snapping {
    scroll-snap-type: none !important;
  }

  /* A stationary sizer clips the moving layer, keeping scrollbar metrics stable. */
  .ds-scroll--elastic > .ds-scroll__content {
    width: max-content;
    min-width: 100%;
    overflow: clip;
  }

  .ds-scroll.ds-scroll--dragging,
  .ds-scroll.ds-scroll--dragging * {
    cursor: grabbing;
  }

  .ds-scroll::-webkit-scrollbar {
    width: var(--ds-scroll-thickness, 8px);
    height: var(--ds-scroll-thickness, 8px);
  }

  .ds-scroll::-webkit-scrollbar-track {
    border-radius: var(--ds-scroll-thumb-radius, 999px);
    background-color: var(--ds-scroll-resolved-track-color);
  }

  .ds-scroll::-webkit-scrollbar-track-piece {
    border-radius: var(--ds-scroll-thumb-radius, 999px);
  }

  .ds-scroll::-webkit-scrollbar-thumb {
    min-width: var(--ds-scroll-thumb-min-size, 40px);
    min-height: var(--ds-scroll-thumb-min-size, 40px);
    border-radius: var(--ds-scroll-thumb-radius, 999px);
    background-color: var(--ds-scroll-resolved-thumb-color);
  }

  .ds-scroll::-webkit-scrollbar-thumb:hover,
  .ds-scroll::-webkit-scrollbar-thumb:active,
  .ds-scroll::-webkit-scrollbar-thumb:window-inactive {
    background-color: var(--ds-scroll-resolved-thumb-hover-color);
  }

  .ds-scroll::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
  }

  /* Chrome supports both APIs. Reset the standard properties there so they do
     not override the WebKit pseudo-elements and their tokenized thickness. */
  @supports selector(::-webkit-scrollbar) {
    .ds-scroll {
      scrollbar-color: auto;
      scrollbar-width: auto;
    }
  }
`;

/**
 * Attribute component that installs the DDS scrollbar styles and adds the
 * public `ds-scroll` class to its scrollable host element.
 *
 * The consumer remains responsible for defining overflow and dimensions.
 * Opt into horizontal mouse/pen dragging with `dragScroll`. Touch is always left to
 * the browser's native scrolling -- a custom JS reimplementation has to fight the
 * OS's own touch-gesture arbitration (edge-swipe-back, etc.), where native scrolling
 * just works. `elasticScroll` adds a rubber-band rebound at either edge for mouse/pen
 * drags; it requires a direct `.ds-scroll__content` sizer containing `.ds-scroll__elastic-layer`.
 * `snapScroll` smoothly settles a mouse/pen drag on the nearest child that declares
 * `scroll-snap-align` -- native CSS scroll-snap (e.g. `scroll-snap-align: center`)
 * already gives touch the same settle-at-rest behavior for free.
 */
@Component({
  selector: '[dsScroll]',
  standalone: true,
  template: '<ng-content />',
  host: {
    class: DS_SCROLL_CLASS,
    '[class.ds-scroll--inverse]': 'mode === "inverse"',
    '[class.ds-scroll--draggable]': 'dragScroll',
    '[class.ds-scroll--elastic]': 'dragScroll && elasticScroll',
    '[class.ds-scroll--snap-enabled]': 'snapScroll',
    '[class.ds-scroll--dragging]': 'dragging',
    '[class.ds-scroll--snapping]': 'snapping',
  },
  encapsulation: ViewEncapsulation.None,
  styles: [DS_SCROLL_STYLES],
})
export class DsScrollComponent implements OnChanges, OnDestroy {
  mode: DsScrollMode = 'normal';
  dragging = false;
  snapping = false;

  @Input({ transform: booleanAttribute }) dragScroll = false;
  @Input({ transform: booleanAttribute }) elasticScroll = false;
  @Input({ transform: booleanAttribute }) snapScroll = false;

  private pointerId: number | null = null;
  private pointerStartX = 0;
  private initialScrollLeft = 0;
  private activationDistance = 0;
  private suppressPointerClick = false;
  private elasticLayer: HTMLElement | null = null;
  private elasticOffset = 0;
  private elasticMaxDistance = 0;
  private elasticResistance = 0;
  private elasticReturnDuration = 0;
  private snapFrame: number | null = null;
  private snapEndListener: (() => void) | null = null;
  private reboundAnimation: Animation | null = null;

  // Capture runs before a projected card/button can emit its action.
  private readonly preventDragClick = (event: MouseEvent): void => {
    if (!this.suppressPointerClick || event.detail === 0) return;
    this.suppressPointerClick = false;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {
    this.elementRef.nativeElement.addEventListener('click', this.preventDragClick, true);
  }

  @Input()
  set dsScroll(value: DsScrollMode | '') {
    this.mode = value === 'inverse' ? 'inverse' : 'normal';
  }

  ngOnChanges(): void {
    if (!this.dragScroll || !this.elasticScroll) {
      this.cancelMotion();
      this.elasticLayer = null;
    }
    if (!this.snapScroll) this.cancelSnap();
    if (!this.dragScroll) {
      this.endDrag(false);
      this.suppressPointerClick = false;
    }
  }

  ngOnDestroy(): void {
    this.cancelMotion();
    this.endDrag(false);
    this.elementRef.nativeElement.removeEventListener('click', this.preventDragClick, true);
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    const host = this.elementRef.nativeElement;
    if (this.pointerId === null) this.suppressPointerClick = false;
    if (
      !this.dragScroll || this.pointerId !== null || event.defaultPrevented
      || event.pointerType === 'touch' || !event.isPrimary || event.button !== 0
      || host.scrollWidth <= host.clientWidth
    ) return;

    const target = event.target as Element | null;
    if (target?.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])')) return;

    // Keep the native scrollbar thumb and arrow buttons independent of content dragging.
    const bounds = host.getBoundingClientRect();
    if (
      event.clientY >= bounds.top + host.clientTop + host.clientHeight
      || event.clientX >= bounds.left + host.clientLeft + host.clientWidth
    ) return;

    this.cancelMotion();
    this.elasticLayer = this.elasticScroll
      ? host.querySelector<HTMLElement>(':scope > .ds-scroll__content > .ds-scroll__elastic-layer')
      : null;
    this.pointerId = event.pointerId;
    this.pointerStartX = event.clientX;
    this.initialScrollLeft = host.scrollLeft;
    this.activationDistance = this.readToken('--ds-scroll-drag-activation-distance');
    this.elasticMaxDistance = this.readToken('--ds-scroll-elastic-max-distance');
    this.elasticResistance = this.readToken('--ds-scroll-elastic-resistance-scale');
    this.elasticReturnDuration = this.readToken('--ds-scroll-elastic-return-duration');
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) return;
    if ((event.buttons & 1) === 0) {
      this.endDrag();
      this.suppressPointerClick = false;
      return;
    }

    const deltaX = event.clientX - this.pointerStartX;
    if (!this.dragging) {
      if (Math.abs(deltaX) <= this.activationDistance) return;
      this.dragging = true;
      this.elementRef.nativeElement.classList.add('ds-scroll--dragging');
      this.suppressPointerClick = true;
      // Delay capture until dragging so a normal click still reaches the card.
      try {
        this.elementRef.nativeElement.setPointerCapture(event.pointerId);
      } catch {
        // Document listeners also handle synthetic events and browsers without capture.
      }
    }

    event.preventDefault();
    this.setScrollPosition(this.initialScrollLeft - deltaX);
  }

  @HostListener('document:pointerup', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) return;
    const snap = this.snapScroll && this.dragging && this.elasticLayer !== null && this.elasticOffset === 0;
    if (snap) {
      this.snapping = true;
      this.elementRef.nativeElement.classList.add('ds-scroll--snapping');
    }
    this.endDrag();
    if (snap && this.snapToNearest()) return;
    if (snap) this.finishSnap();
  }

  @HostListener('document:pointercancel', ['$event'])
  @HostListener('lostpointercapture', ['$event'])
  onPointerCancel(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) return;
    this.endDrag();
    this.suppressPointerClick = false;
  }

  @HostListener('window:blur')
  onWindowBlur(): void {
    this.cancelMotion();
    this.endDrag();
    this.suppressPointerClick = false;
  }

  @HostListener('dragstart', ['$event'])
  onDragStart(event: DragEvent): void {
    if (this.pointerId !== null) event.preventDefault();
  }

  @HostListener('wheel')
  onWheel(): void {
    if (this.reboundAnimation !== null) this.cancelMotion();
  }

  private readToken(name: string): number {
    const host = this.elementRef.nativeElement;
    const value = Number.parseFloat(host.ownerDocument.defaultView?.getComputedStyle(host).getPropertyValue(name) ?? '');
    return Number.isFinite(value) ? Math.max(0, value) : 0;
  }

  private setScrollPosition(position: number): boolean {
    const host = this.elementRef.nativeElement;
    const maximum = Math.max(0, host.scrollWidth - host.clientWidth);
    const clamped = Math.min(maximum, Math.max(0, position));
    host.scrollLeft = clamped;
    const overshoot = position - clamped;
    const limit = this.elasticMaxDistance;
    const resistance = this.elasticResistance;
    const offset = limit > 0 && resistance > 0
      ? -limit * overshoot / (Math.abs(overshoot) + limit / resistance)
      : 0;
    this.setElasticOffset(offset);
    return overshoot !== 0;
  }

  private setElasticOffset(offset: number): void {
    this.elasticOffset = this.elasticLayer ? offset : 0;
    if (this.elasticLayer) {
      this.elasticLayer.style.transform = offset === 0 ? '' : `translate3d(${offset}px, 0, 0)`;
    }
  }

  private prefersReducedMotion(): boolean {
    return this.elementRef.nativeElement.ownerDocument.defaultView
      ?.matchMedia('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  private rebound(): void {
    const layer = this.elasticLayer;
    const offset = this.elasticOffset;
    if (!layer || offset === 0) return;
    this.reboundAnimation?.cancel();
    if (this.elasticReturnDuration > 0 && !this.prefersReducedMotion() && typeof layer.animate === 'function') {
      const animation = layer.animate([
        { transform: `translate3d(${offset}px, 0, 0)` },
        { transform: 'translate3d(0px, 0, 0)' },
      ], { duration: this.elasticReturnDuration, easing: 'ease-out' });
      this.reboundAnimation = animation;
      animation.onfinish = () => {
        if (this.reboundAnimation === animation) this.reboundAnimation = null;
      };
    }
    this.setElasticOffset(0);
  }

  private snapToNearest(): boolean {
    const host = this.elementRef.nativeElement;
    const view = host.ownerDocument.defaultView;
    const layer = this.elasticLayer;
    if (!view || !layer) return false;

    const candidates = Array.from(layer.children).filter((child): child is HTMLElement =>
      child instanceof HTMLElement && view.getComputedStyle(child).scrollSnapAlign !== 'none'
    );
    if (candidates.length === 0) return false;

    const hostBounds = host.getBoundingClientRect();
    const hostStyles = view.getComputedStyle(host);
    const paddingStart = Number.parseFloat(hostStyles.scrollPaddingLeft) || 0;
    const paddingEnd = Number.parseFloat(hostStyles.scrollPaddingRight) || 0;
    const snapportCenter = paddingStart + (host.clientWidth - paddingStart - paddingEnd) / 2;
    const maximum = Math.max(0, host.scrollWidth - host.clientWidth);
    const targets = candidates.map((candidate) => {
      const bounds = candidate.getBoundingClientRect();
      const contentCenter = bounds.left - hostBounds.left + host.scrollLeft + bounds.width / 2;
      return Math.min(maximum, Math.max(0, contentCenter - snapportCenter));
    });
    const target = targets.reduce((nearest, candidate) =>
      Math.abs(candidate - host.scrollLeft) < Math.abs(nearest - host.scrollLeft) ? candidate : nearest
    );

    if (this.prefersReducedMotion() || Math.abs(target - host.scrollLeft) < 0.5) {
      host.scrollLeft = target;
      this.finishSnap();
      return true;
    }

    const finish = (): void => {
      if (Math.abs(target - host.scrollLeft) < 0.5) host.scrollLeft = target;
      this.finishSnap();
    };
    this.snapEndListener = finish;
    host.addEventListener('scrollend', finish, { once: true });
    host.scrollTo({ left: target, behavior: 'smooth' });

    const monitor = (): void => {
      this.snapFrame = null;
      if (!this.snapping) return;
      if (Math.abs(target - host.scrollLeft) < 0.5) {
        host.scrollLeft = target;
        this.finishSnap();
        return;
      }
      this.snapFrame = view.requestAnimationFrame(monitor);
    };
    if (this.snapping) this.snapFrame = view.requestAnimationFrame(monitor);
    return true;
  }

  private finishSnap(): void {
    const host = this.elementRef.nativeElement;
    const view = host.ownerDocument.defaultView;
    if (this.snapFrame !== null) view?.cancelAnimationFrame(this.snapFrame);
    this.snapFrame = null;
    if (this.snapEndListener) host.removeEventListener('scrollend', this.snapEndListener);
    this.snapEndListener = null;
    this.snapping = false;
    host.classList.remove('ds-scroll--snapping');
  }

  private cancelSnap(): void {
    const host = this.elementRef.nativeElement;
    if (this.snapping) host.scrollTo({ left: host.scrollLeft, behavior: 'auto' });
    this.finishSnap();
  }

  private cancelMotion(): void {
    this.cancelSnap();
    this.reboundAnimation?.cancel();
    this.reboundAnimation = null;
    this.setElasticOffset(0);
  }

  private endDrag(animate = true): void {
    const pointerId = this.pointerId;
    this.pointerId = null;
    this.dragging = false;
    const host = this.elementRef.nativeElement;
    host.classList.remove('ds-scroll--dragging');
    if (pointerId !== null && host.hasPointerCapture?.(pointerId)) host.releasePointerCapture(pointerId);
    if (animate) this.rebound();
  }
}
